// Repair ticket creation form — /repair/new?claim=:claimId
// Supports walk-in and from warranty claim (auto-fill).
import { useState, useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronLeft, Plus, Trash2 } from "lucide-react";
import { repairApi } from "./repair-api";
import { apiGet } from "@/lib/api/client";

// Mot dong "Danh muc sua chua" go tay: ten - SL - don vi tinh - don gia.
// Thanh tien = SL x don gia, tinh khi hien thi.
interface RepairItemRow {
  name: string;
  quantity: string;
  unit: string;
  unitPrice: string;
}

const EMPTY_ITEM: RepairItemRow = { name: "", quantity: "1", unit: "", unitPrice: "" };

const fmtMoney = (n: number) => n.toLocaleString("vi-VN");

interface ClaimPreview {
  id: string;
  code: string;
  serialNo: string;
  productName: string;
  variantSku: string;
  customerId?: string;
  customerName?: string;
  issueDesc: string;
}

type Source = "walkin" | "claim";

export function RepairFormPage() {
  const navigate = useNavigate();
  // Read ?claim= query param
  const search = useSearch({ strict: false }) as { claim?: string };
  const claimId = search?.claim as string | undefined;

  const [source, setSource] = useState<Source>(claimId ? "claim" : "walkin");
  const [form, setForm] = useState({
    deviceDesc: "",
    accessories: "",
    issueDesc: "",
    estimatedCost: "",
    customerPhone: "",
    customerName: "",
    serialId: "",
    technicianId: "",
  });
  // Danh muc sua chua (nhieu dong) + cong sua — luu thanh repair_parts (dong
  // go tay) + 1 dong repair_labor sau khi tao phieu.
  const [items, setItems] = useState<RepairItemRow[]>([{ ...EMPTY_ITEM }]);
  const [laborCost, setLaborCost] = useState("");

  const setItem = (i: number, patch: Partial<RepairItemRow>) =>
    setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addItemRow = () => setItems((rows) => [...rows, { ...EMPTY_ITEM }]);
  const removeItemRow = (i: number) => setItems((rows) => rows.filter((_, idx) => idx !== i));

  const filledItems = items.filter((r) => r.name.trim() !== "");
  const itemsTotal = filledItems.reduce(
    (sum, r) => sum + (parseFloat(r.unitPrice) || 0) * (parseInt(r.quantity, 10) || 0),
    0,
  );
  const laborNum = parseFloat(laborCost) || 0;

  // Load claim if provided.
  const { data: claim } = useQuery({
    queryKey: ["warranty-claim-preview", claimId],
    queryFn: () => apiGet<ClaimPreview>(`/v1/warranty/claims/${claimId}`),
    enabled: !!claimId,
  });

  useEffect(() => {
    if (claim) {
      setSource("claim");
      setForm((f) => ({
        ...f,
        issueDesc: claim.issueDesc || f.issueDesc,
        deviceDesc: `${claim.productName} (${claim.variantSku}) SN:${claim.serialNo}`,
        customerName: claim.customerName ?? f.customerName,
      }));
    }
  }, [claim]);

  const create = useMutation({
    mutationFn: async () => {
      const ticket =
        source === "claim" && claimId
          ? await repairApi.createTicket({
              warrantyClaimId: claimId,
              issueDesc: form.issueDesc,
              accessories: form.accessories || undefined,
              estimatedCost: form.estimatedCost || undefined,
              technicianId: form.technicianId || undefined,
            })
          : await repairApi.createTicket({
              customerPhone: form.customerPhone || undefined,
              customerName: form.customerName || undefined,
              deviceDesc: form.deviceDesc,
              accessories: form.accessories || undefined,
              issueDesc: form.issueDesc,
              estimatedCost: form.estimatedCost || undefined,
              technicianId: form.technicianId || undefined,
            });

      // Ghi danh muc sua chua + cong sua NGAY SAU khi tao phieu. Loi le te o
      // buoc nay khong chan dieu huong — phieu da ton tai, bo sung duoc o trang
      // chi tiet; gom loi de bao.
      const errors: string[] = [];
      for (const r of filledItems) {
        try {
          await repairApi.addPart(ticket.id, {
            name: r.name.trim(),
            unit: r.unit.trim(),
            quantity: parseInt(r.quantity, 10) || 1,
            unitPrice: r.unitPrice || "0",
          });
        } catch {
          errors.push(r.name.trim());
        }
      }
      if (laborNum > 0) {
        try {
          await repairApi.addLabor(ticket.id, { description: "Công sửa", amount: String(laborNum) });
        } catch {
          errors.push("Công sửa");
        }
      }
      return { ticket, errors };
    },
    onSuccess: ({ ticket, errors }) => {
      if (errors.length > 0) {
        alert(`Phiếu ${ticket.code} đã tạo nhưng lỗi khi thêm: ${errors.join(", ")}. Bổ sung lại ở trang chi tiết.`);
      }
      navigate({ to: `/repair/${ticket.id}` as string });
    },
  });

  function validate(): string | null {
    if (!form.issueDesc.trim()) return "Vui lòng nhập mô tả lỗi";
    if (source === "walkin") {
      if (!form.deviceDesc.trim()) return "Vui lòng nhập tên thiết bị";
      if (!form.customerPhone.trim() && !form.customerName.trim()) {
        return "Vui lòng nhập số điện thoại hoặc tên khách hàng";
      }
    }
    return null;
  }

  const validationError = validate();

  return (
    <div className="mx-auto max-w-xl space-y-6">
      {/* Header */}
      <div>
        <button onClick={() => navigate({ to: "/repair" })} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-1">
          <ChevronLeft size={14} /> Danh sách sửa chữa
        </button>
        <h1 className="text-2xl font-bold">Tạo phiếu sửa chữa</h1>
      </div>

      {/* Source toggle */}
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <p className="text-sm font-medium text-muted-foreground">Nguồn phiếu</p>
        <div className="flex gap-2">
          {(["walkin", "claim"] as Source[]).map((s) => (
            <button
              key={s}
              onClick={() => setSource(s)}
              className={`flex-1 rounded-md border py-2 text-sm font-medium transition-colors ${
                source === s ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent"
              }`}
            >
              {s === "walkin" ? "Walk-in" : "Từ phiếu bảo hành"}
            </button>
          ))}
        </div>

        {/* Claim preview */}
        {source === "claim" && claim && (
          <div className="rounded-lg bg-muted/40 p-3 text-sm space-y-1">
            <p className="font-medium">{claim.code}</p>
            <p className="text-muted-foreground">{claim.productName} · {claim.variantSku} · SN:{claim.serialNo}</p>
            {claim.customerName && <p className="text-muted-foreground">Khách: {claim.customerName}</p>}
          </div>
        )}

        {source === "claim" && !claimId && (
          <p className="text-sm text-muted-foreground">Mở phiếu này từ trang chi tiết phiếu bảo hành để tự động điền thông tin.</p>
        )}
      </div>

      {/* Walk-in customer + device */}
      {source === "walkin" && (
        <div className="rounded-xl border bg-card p-5 space-y-4">
          <p className="text-sm font-semibold">Khách hàng</p>
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">Số điện thoại</span>
            <input
              type="tel"
              className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
              value={form.customerPhone}
              onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}
              placeholder="0912..."
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">Tên khách hàng</span>
            <input
              type="text"
              className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
              value={form.customerName}
              onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
              placeholder="Nguyễn Văn A"
            />
          </label>

          <p className="text-sm font-semibold pt-2">Thiết bị</p>
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">Tên thiết bị <span className="text-destructive">*</span></span>
            <input
              type="text"
              className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
              value={form.deviceDesc}
              onChange={(e) => setForm((f) => ({ ...f, deviceDesc: e.target.value }))}
              placeholder="VD: iPhone 14 Pro Max 256GB, màu xanh"
            />
          </label>
        </div>
      )}

      {/* Common fields */}
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <p className="text-sm font-semibold">Thông tin sửa chữa</p>

        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">Mô tả lỗi <span className="text-destructive">*</span></span>
          <textarea
            rows={3}
            className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
            value={form.issueDesc}
            onChange={(e) => setForm((f) => ({ ...f, issueDesc: e.target.value }))}
            placeholder="Mô tả tình trạng lỗi của thiết bị..."
          />
        </label>

        {/* Danh muc sua chua — nhieu dong: ten, SL, DVT, don gia, thanh tien */}
        <div className="space-y-2">
          <span className="text-sm text-muted-foreground">Danh mục sửa chữa</span>
          <div className="space-y-2">
            {items.map((r, i) => {
              const line = (parseFloat(r.unitPrice) || 0) * (parseInt(r.quantity, 10) || 0);
              return (
                <div key={i} className="grid grid-cols-[1fr_64px_72px_110px_92px_28px] items-center gap-1.5">
                  <input
                    type="text"
                    placeholder="Tên sản phẩm / dịch vụ…"
                    className="rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none"
                    value={r.name}
                    onChange={(e) => setItem(i, { name: e.target.value })}
                  />
                  <input
                    type="number"
                    min={1}
                    placeholder="SL"
                    className="rounded-md border bg-background px-2 py-1.5 text-sm text-right focus:outline-none"
                    value={r.quantity}
                    onChange={(e) => setItem(i, { quantity: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="ĐVT"
                    className="rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none"
                    value={r.unit}
                    onChange={(e) => setItem(i, { unit: e.target.value })}
                  />
                  <input
                    type="number"
                    min={0}
                    placeholder="Đơn giá"
                    className="rounded-md border bg-background px-2 py-1.5 text-sm text-right focus:outline-none"
                    value={r.unitPrice}
                    onChange={(e) => setItem(i, { unitPrice: e.target.value })}
                  />
                  <span className="text-right text-sm font-mono">{line > 0 ? fmtMoney(line) : "—"}</span>
                  <button
                    type="button"
                    onClick={() => removeItemRow(i)}
                    disabled={items.length === 1}
                    className="text-destructive disabled:opacity-30"
                    aria-label="Xóa dòng"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={addItemRow}
            className="flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <Plus size={14} /> Thêm dòng
          </button>
        </div>

        {/* Cong sua — tien cong, tuy chon */}
        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">Công sửa (VNĐ)</span>
          <input
            type="number"
            min={0}
            className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
            value={laborCost}
            onChange={(e) => setLaborCost(e.target.value)}
            placeholder="0"
          />
        </label>

        {(itemsTotal > 0 || laborNum > 0) && (
          <p className="text-sm text-right">
            Tổng dự tính:{" "}
            <span className="font-semibold text-primary">{fmtMoney(itemsTotal + laborNum)} ₫</span>
            {itemsTotal > 0 && laborNum > 0 && (
              <span className="text-muted-foreground text-xs"> (danh mục {fmtMoney(itemsTotal)} + công {fmtMoney(laborNum)})</span>
            )}
          </p>
        )}

        <label className="block space-y-1 text-sm">
          <span className="text-muted-foreground">Chi phí dự kiến báo khách (VNĐ)</span>
          <input
            type="number"
            min={0}
            className="w-full rounded-md border bg-background px-3 py-2 focus:outline-none"
            value={form.estimatedCost}
            onChange={(e) => setForm((f) => ({ ...f, estimatedCost: e.target.value }))}
            placeholder="0"
          />
        </label>
      </div>

      {/* Errors */}
      {create.isError && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Tạo phiếu thất bại. Vui lòng kiểm tra lại thông tin.
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button onClick={() => navigate({ to: "/repair" })} className="rounded-md border px-5 py-2 text-sm">
          Huỷ
        </button>
        <button
          onClick={() => create.mutate()}
          disabled={!!validationError || create.isPending}
          title={validationError ?? undefined}
          className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40"
        >
          {create.isPending ? "Đang tạo..." : "Tạo phiếu sửa chữa"}
        </button>
      </div>
    </div>
  );
}
